// BaseLimitFeedbackHost.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Utilities
import {

  openBasePlanSettings,
  subscribeBaseLimitFeedback,
} from 'utilities/baseLimitFeedback';

// Services
import { getBaseEntitlements } from 'services/bases/BaseEntitlementsService';

// Components
import LimitReachedModal from 'components/limit/LimitReachedModal';

/**
 * BaseLimitFeedbackHost
 *
 * Renders the global base limit modal for service-layer failures.
 *
 * @returns {ReactNode} The rendered component.
 */
const BaseLimitFeedbackHost = () => {

  // ----- CONTEXT ----- //

  const { activeBase } = useBase();

  // ----- STATE ----- //

  const [limitState, setLimitState] = useState(null);
  const [actorCanUpgrade, setActorCanUpgrade] = useState(false);

  // ----- MEMOS ----- //

  const clearLimitState = useCallback(() => {
    setLimitState(null);
    setActorCanUpgrade(false);
  }, []);

  // ----- HANDLERS ----- //

  /**
   * Handles upgrade plan.
   */

  const handleUpgradePlan = useCallback(() => {
    clearLimitState();
    openBasePlanSettings();
  }, [clearLimitState]);

  // ----- EFFECTS ----- //

  useEffect(() => {

    return subscribeBaseLimitFeedback(async ({ error, baseKey }) => {
      const cleanedBaseKey = String(
        baseKey || activeBase?.key || activeBase?.id || ''
      ).trim();

      setLimitState({
        limit: error?.limit ?? null,
        limitType: error?.limitType || 'storage',
        message: error?.message || 'Base limit reached.',
        used: error?.used ?? null,
      });

      if (!cleanedBaseKey) {
        setActorCanUpgrade(false);
        return;
      }

      try {
        const entitlements = await getBaseEntitlements({ baseKey: cleanedBaseKey });
        setActorCanUpgrade(Boolean(entitlements?.actor?.canUpgradePlan));
      } catch {
        setActorCanUpgrade(false);
      }
    });
  }, [activeBase?.id, activeBase?.key]);

  // ----- RENDER ----- //

  return (
    <LimitReachedModal
      actorCanUpgrade={actorCanUpgrade}
      isOpen={Boolean(limitState)}
      limit={limitState?.limit}
      limitType={limitState?.limitType}
      message={limitState?.message}
      used={limitState?.used}
      onAdminAction={handleUpgradePlan}
      onClose={clearLimitState}
    />
  );
};

// Dev Tools
BaseLimitFeedbackHost.displayName = 'BaseLimitFeedbackHost';

export default BaseLimitFeedbackHost;
